import { useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";

/**
 * Hook para checagem de entitlement do usuário logado.
 * Retorna { entitlement, loading, refresh, guard } — onde guard é uma
 * função que decide se a ação está liberada ou se precisa de paywall.
 *
 * Regra de ouro (Seção 59): NUNCA envie amount do frontend.
 * Envie service_code + quantity. O backend calcula o preço.
 */
export function useEntitlement(serviceCode) {
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!serviceCode) return;
    setLoading(true);
    try {
      const r = await api.get(`/billing/entitlements/${serviceCode}`);
      setEntitlement(r.data);
    } catch { setEntitlement(null); }
    finally { setLoading(false); }
  }, [serviceCode]);

  useEffect(() => { refresh(); }, [refresh]);

  /**
   * Retorna { allowed, needsPaywall, overageAmount, remaining, always_paid }.
   * - Serviço unlimited: sempre allowed
   * - always_paid: sempre needsPaywall (custo real por unidade)
   * - remaining >= quantity: allowed
   * - remaining < quantity: needsPaywall (overage)
   */
  const guard = useCallback((quantity = 1) => {
    if (!entitlement) return { allowed: false, loading: true };
    if (entitlement.status !== "ACTIVE") {
      return { allowed: false, needsPaywall: true, reason: entitlement.unavailable_reason };
    }
    if (entitlement.always_paid) {
      return {
        allowed: false, needsPaywall: true, always_paid: true,
        overageAmount: quantity * (entitlement.overage_price || 0),
      };
    }
    if (entitlement.unlimited) {
      return { allowed: true };
    }
    const remaining = entitlement.remaining ?? 0;
    if (remaining >= quantity) {
      return { allowed: true, remaining };
    }
    const overage_units = quantity - remaining;
    return {
      allowed: false, needsPaywall: true,
      overage_units,
      overageAmount: overage_units * (entitlement.overage_price || 0),
      remaining,
    };
  }, [entitlement]);

  return { entitlement, loading, refresh, guard };
}
